import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { Box, ShadowBox, Text } from '@components/Restyle';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';

export type FilesFilterPopupProps = {
  isMyUploads?: boolean;
  setIsMyUploads?: (value: boolean) => void;
  filesFilter?: string[];
  setFilesFilter: (value: string[]) => void;
  maxHeight?: number;
};

export const FilesFilterPopup: React.FC<FilesFilterPopupProps> = ({
  isMyUploads = false,
  setIsMyUploads,
  filesFilter = [],
  setFilesFilter,
  maxHeight = 180,
}) => {
  const { t } = useTranslation();
  const [hoveredId, setHoveredId] = useState('');
  const hasActiveFilters = isMyUploads || filesFilter.length > 0;

  const toggle = (value: string) => {
    if (filesFilter.includes(value)) {
      setFilesFilter(filesFilter.filter((v) => v !== value));
    } else {
      setFilesFilter([...filesFilter, value]);
    }
  };

  const items = [
    {
      id: 'my-upload',
      label: t(
        'models:projects.projectDetails.filesWithMedia.filter.myUploads'
      ),
      checked: isMyUploads,
      onPress: () => setIsMyUploads?.(!isMyUploads),
    },
    {
      id: 'media',
      label: t('models:projects.projectDetails.tabs.media'),
      checked: filesFilter.includes('Media'),
      onPress: () => toggle('Media'),
    },
    {
      id: 'documents',
      label: t(
        'models:projects.projectDetails.filesWithMedia.filter.documents'
      ),
      checked: filesFilter.includes('Files'),
      onPress: () => toggle('Files'),
    },
  ];

  return (
    <ShadowBox
      width={220}
      alignItems='flex-start'
      justifyContent='flex-start'
      borderWidth={1}
      borderRadius='xs'
      variant='base'
      backgroundColor='white'>
      <Box mt='xs' style={{ maxHeight }} width='100%'>
        {items.map((item) => (
          <TouchableOpacity
            key={item.id}
            accessibilityRole='button'
            accessibilityLabel={item.label}
            onPress={item.onPress}>
            <Box
              height={36}
              px='m'
              flexDirection='row'
              alignItems='center'
              backgroundColor={hoveredId === item.id ? 'grey01' : 'white'}
              onMouseEnter={() => {
                setHoveredId(item.id);
              }}
              onMouseLeave={() => {
                setHoveredId('');
              }}>
              <Box marginRight='xs'>
                <CheckBoxButton checked={item.checked} />
              </Box>
              <Text variant='webBodySecondary' color='textPrimary'>
                {item.label}
              </Text>
            </Box>
          </TouchableOpacity>
        ))}
        <Box borderTopWidth={1} borderTopColor='grey02' marginTop='xs' />
        <TouchableOpacity
          accessibilityRole='button'
          accessibilityLabel={t('shared:clearAll')}
          disabled={!hasActiveFilters}
          onPress={() => {
            setIsMyUploads?.(false);
            setFilesFilter([]);
          }}>
          <Box height={36} px='m' justifyContent='center' alignItems='center'>
            <Text
              variant='webBodySecondary'
              color={hasActiveFilters ? 'textPrimary' : 'grey04'}>
              {t('shared:clearAll')}
            </Text>
          </Box>
        </TouchableOpacity>
      </Box>
    </ShadowBox>
  );
};
